import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_LOGO = process.env.REACT_APP_DEFAULT_LOGO_URL;
const SUN_ICON = process.env.REACT_APP_SUN_ICON_URL;
const FACEBOOK_PROFILE = process.env.REACT_APP_FACEBOOK_PROFILE_URL;
const FACEBOOK_PHOTOS = process.env.REACT_APP_FACEBOOK_PHOTOS_URL;

// Helper function to get full image URL
const getImageUrl = (path, options = {}) => {
  if (!path) return '';
  
  // If already a full Cloudinary URL, add optimizations
  if (path.includes('res.cloudinary.com') && path.includes('/upload/')) {
    // Default transformations for performance
    const transforms = options.transforms || 'w_800,q_auto,f_auto';
    // Avoid double-adding transforms
    if (!path.includes('/w_') && !path.includes('/q_auto') && !path.includes('/f_auto')) {
      return path.replace('/upload/', `/upload/${transforms}/`);
    }
    return path;
  }
  
  // If other full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // If relative path (legacy local uploads), prepend BACKEND_URL
  return `${BACKEND_URL}${path}`;
};

const Home = () => {
  const [events, setEvents] = useState([]);
  const [team, setTeam] = useState([]);
  const [settings, setSettings] = useState(null);
  const [partners, setPartners] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [heroGallery, setHeroGallery] = useState([]);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isDone: false });
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFAB, setShowFAB] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    fetchData();
    trackVisitor();
    
    // Handle scroll for floating button
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const trackVisitor = async () => {
    try {
      await axios.post(`${API}/visitor/track`);
      const statsRes = await axios.get(`${API}/visitor/stats`);
      setVisitorStats(statsRes.data);
    } catch (error) {
      console.error('Error tracking visitor:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [eventsRes, teamRes, settingsRes, partnersRes, albumsRes, heroGalleryRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/team`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/partners`),
        axios.get(`${API}/albums`),
        axios.get(`${API}/hero-gallery`)
      ]);
      setEvents(eventsRes.data);
      setTeam(teamRes.data);
      setSettings(settingsRes.data);
      setPartners(partnersRes.data);
      setAlbums(albumsRes.data);
      setHeroGallery(heroGalleryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (!settings?.countdown_date) return;

    const target = new Date(settings.countdown_date).getTime();
    
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const diff = target - now;

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isDone: true });
        clearInterval(interval);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds, isDone: false });
    }, 1000);

    return () => clearInterval(interval);
  }, [settings]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent double submit
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactForm({ name: '', email: '', message: '' });
      setContactSuccess(true);
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setContactSuccess(false);
      }, 5000);
    } catch (error) {
      toast.error('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      toast.success('🎉 Bedankt voor je inschrijving! Je ontvangt nu alle updates.');
      setNewsletterEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#0a56c2] shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <a href="#" onClick={(e) => { e.preventDefault(); scrollToTop(); }} className="cursor-pointer">
                <img 
                  src={getImageUrl(settings?.logo_url) || DEFAULT_LOGO} 
                  alt="Feestcomité Leupegem logo - Terug naar boven" 
                  className="h-16 w-auto sm:h-20"
                />
              </a>
            </div>
            <div className="hidden md:flex gap-8">
              <a href="#events" className="text-white hover:text-[#ffe980] font-medium transition-colors">Evenementen</a>
              <a href="#team" className="text-white hover:text-[#ffe980] font-medium transition-colors">Team</a>
              <a href="#partners" className="text-white hover:text-[#ffe980] font-medium transition-colors">Partners</a>
              <a href="#fotos" className="text-white hover:text-[#ffe980] font-medium transition-colors">Foto's</a>
              <a href="#contact" className="text-white hover:text-[#ffe980] font-medium transition-colors">Contact</a>
            </div>
            <div className="flex items-center gap-4">
              {/* Newsletter Icon - Links naar nieuwsbrief sectie */}
              <a 
                href="#newsletter"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('newsletter')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="hidden md:flex items-center gap-2 text-white hover:text-[#ffe980] transition-colors p-2"
                aria-label="Nieuwsbrief"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Nieuwsbrief</span>
              </a>
              
              <a href="/admin" className="hidden sm:block text-sm text-white/80 hover:text-white transition-colors">Admin</a>
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-white p-2 rounded-md hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#083d8a] border-t border-white/10 animate-in slide-in-from-top-2 duration-200">
            <div className="px-4 py-2 space-y-1">
              <a 
                href="#events" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-white hover:text-[#ffe980] hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
              >
                Evenementen
              </a>
              <a 
                href="#team" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-white hover:text-[#ffe980] hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
              >
                Team
              </a>
              <a 
                href="#partners" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-white hover:text-[#ffe980] hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
              >
                Partners
              </a>
              <a 
                href="#fotos" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-white hover:text-[#ffe980] hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
              >
                Foto's
              </a>
              <a 
                href="#contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-white hover:text-[#ffe980] hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
              >
                Contact
              </a>
              <div className="border-t border-white/10 mt-2 pt-2">
                <a 
                  href="/admin" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-white/70 hover:text-white hover:bg-white/10 active:bg-white/20 font-medium py-3 px-3 min-h-[44px] flex items-center rounded-lg transition-all duration-150"
                >
                  Admin
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-yellow-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-block mb-8">
            <span className="text-base sm:text-lg font-bold tracking-wider text-[#0a56c2] uppercase">Ready to Party!</span>
          </div>
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-extrabold text-gray-900 mb-8" data-testid="hero-title">
            Feestcomité<br />Leupegem
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 max-w-2xl mx-auto mb-8">
            Wij organiseren met veel liefde en plezier de mooiste evenementen in Leupegem
          </p>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="hero-gallery">
            {heroGallery.map((item, index) => (
              <div 
                key={item.id} 
                className="relative overflow-hidden rounded-2xl group card-hover h-64 md:h-80"
                data-testid={`hero-gallery-item-${index}`}
              >
                <img 
                  src={getImageUrl(item.image_url)}
                  alt={`${item.title} - Sfeerbeeld van Feestcomité Leupegem`}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-xl font-bold">{item.title}</h3>
                  <p className="text-sm opacity-90">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Countdown Section - Nu gebruikt als bedankboodschap met nieuwsbrief CTA */}
      {settings && (
        <section className="py-12 px-4 sm:px-6 lg:px-8" data-testid="countdown-section">
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#0a56c2] rounded-2xl p-8 sm:p-12 text-white text-center shadow-xl">
              {/* Show countdown if date is in future, otherwise show teaser */}
              {countdown.isDone || !settings.countdown_date ? (
                <>
                  <div className="text-5xl mb-4">🎉</div>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                    {settings.countdown_title || 'Nieuwe activiteit in voorbereiding'}
                  </h2>
                  <p className="text-lg sm:text-xl opacity-90 leading-relaxed mb-6">
                    {settings.countdown_subtitle || 'Blijf op de hoogte via onze nieuwsbrief!'}
                  </p>
                  <a 
                    href="#newsletter"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('newsletter')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 bg-white text-[#0a56c2] font-bold py-3 px-6 rounded-xl hover:bg-[#ffe980] hover:text-gray-900 transition-all hover:scale-105 shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Schrijf je in voor updates
                  </a>
                </>
              ) : (
                <>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4">{settings.countdown_title}</h2>
                  <p className="text-lg sm:text-xl opacity-90 leading-relaxed mb-6">{settings.countdown_subtitle}</p>
                  <div className="flex justify-center gap-4 sm:gap-8">
                    <div className="text-center">
                      <div className="text-4xl sm:text-5xl font-bold">{countdown.days}</div>
                      <div className="text-sm opacity-75">Dagen</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl sm:text-5xl font-bold">{countdown.hours}</div>
                      <div className="text-sm opacity-75">Uren</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl sm:text-5xl font-bold">{countdown.minutes}</div>
                      <div className="text-sm opacity-75">Minuten</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl sm:text-5xl font-bold">{countdown.seconds}</div>
                      <div className="text-sm opacity-75">Seconden</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Events Section */}
      <section id="events" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-bold tracking-wider text-[#0a56c2] uppercase">Proudly Presenting</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-2" data-testid="events-title">
              Aankomende Activiteiten
            </h2>
          </div>
          
          {/* Filter events: show upcoming first, then past events dimmed */}
          {(() => {
            const parseEventDate = (dateStr) => {
              if (!dateStr) return new Date(0);
              // Parse formats like "18/1/2025" or "18 januari 2025" or "ZONDAG 30 NOV 2026"
              const parts = dateStr.split('/');
              if (parts.length === 3) {
                return new Date(parts[2], parts[1] - 1, parts[0]);
              }
              // Try to parse other date formats
              const parsed = new Date(dateStr);
              return isNaN(parsed.getTime()) ? new Date(0) : parsed;
            };
            
            const isEventPast = (event) => {
              // Check if "AFGELOPEN" is in the title (explicit marker)
              if (event.title && event.title.toUpperCase().includes('AFGELOPEN')) {
                return true;
              }
              // Check date
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              return parseEventDate(event.date) < now;
            };
            
            const upcomingEvents = events.filter(e => !isEventPast(e));
            const pastEvents = events.filter(e => isEventPast(e));
            
            // If no upcoming events, show placeholder
            if (upcomingEvents.length === 0 && pastEvents.length === 0) {
              return (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🎉</div>
                  <p className="text-xl text-gray-600">Nieuwe activiteiten worden binnenkort aangekondigd.</p>
                  <p className="text-gray-500 mt-2">Schrijf je in voor onze nieuwsbrief om als eerste op de hoogte te zijn!</p>
                </div>
              );
            }
            
            if (upcomingEvents.length === 0 && pastEvents.length > 0) {
              return (
                <>
                  <div className="text-center py-8 mb-8 bg-blue-50 rounded-2xl">
                    <div className="text-4xl mb-3">📅</div>
                    <p className="text-lg text-gray-700 font-medium">Nieuwe activiteiten worden binnenkort aangekondigd.</p>
                    <p className="text-gray-500 mt-1">Houd onze nieuwsbrief in de gaten!</p>
                  </div>
                  
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-500 mb-4 text-center">Eerdere activiteiten</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-60">
                      {pastEvents.slice(0, 3).map((event, index) => (
                        <article 
                          key={event.id} 
                          className="bg-white border border-gray-200 rounded-2xl overflow-hidden relative"
                          data-testid={`event-card-past-${index}`}
                        >
                          <div className="absolute top-3 right-3 z-10 px-2 py-1 bg-gray-800 text-white text-xs font-semibold rounded">
                            Afgelopen
                          </div>
                          {event.image_url && (
                            <div className="h-48 overflow-hidden relative grayscale">
                              <img 
                                src={getImageUrl(event.image_url)} 
                                alt={`Afbeelding van afgelopen evenement: ${event.title}`} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="p-6">
                            <span className="inline-block px-3 py-1 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold uppercase mb-3">
                              {event.date} • {event.time}
                            </span>
                            <h4 className="text-xl font-bold text-gray-700 mb-3">{event.title}</h4>
                            <p className="text-gray-500 mb-4">{event.description}</p>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </>
              );
            }
            
            // Show upcoming events normally
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
                {upcomingEvents.map((event, index) => (
              <article 
                key={event.id} 
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden card-hover"
                data-testid={`event-card-${index}`}
              >
                {event.image_url && (
                  <div 
                    className="h-48 overflow-hidden relative group cursor-pointer"
                    onClick={() => setLightboxImage(event.image_url)}
                  >
                    <img 
                      src={getImageUrl(event.image_url)} 
                      alt={`Evenement: ${event.title} - Klik om te vergroten`} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/90 rounded-full p-3">
                        <svg className="w-6 h-6 text-[#0a56c2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <span className="inline-block px-3 py-1 rounded-full bg-[#ffe980] text-gray-900 text-xs font-semibold uppercase mb-3">
                    {event.date} • {event.time}
                  </span>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h4>
                  <p className="text-gray-600 mb-4">{event.description}</p>
                  
                  {/* Nieuwjaarsreceptie registratie knop - UITGESCHAKELD (event afgelopen)
                  {event.id === 'event-001' && (
                    <a 
                      href="/nieuwjaarsreceptie"
                      className="inline-block w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all hover:scale-105 text-center shadow-lg mb-4"
                    >
                      Schrijf je in voor de nieuwjaarsreceptie
                    </a>
                  )}
                  */}
                  
                  {event.buttons && event.buttons.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {event.buttons.map((btn, idx) => (
                        <Button
                          key={idx}
                          variant={idx === 0 ? 'default' : 'outline'}
                          className={idx === 0 ? 'bg-[#0a56c2] hover:bg-[#083d8a] btn-hover' : 'btn-hover'}
                          onClick={() => window.location.href = btn.link}
                          data-testid={`event-button-${index}-${idx}`}
                        >
                          {btn.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#ffe980] p-8 sm:p-12 lg:p-16 rounded-2xl relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#0a56c2] opacity-10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#0a56c2] opacity-10 rounded-full -ml-24 -mb-24"></div>
            <div className="max-w-2xl mx-auto text-center relative z-10">
              <div className="inline-block mb-4">
                <svg className="w-16 h-16 text-[#0a56c2] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
                📬 Blijf op de hoogte!
              </h3>
              <p className="text-lg sm:text-xl text-gray-800 mb-2 font-medium">
                Ontvang als eerste info over nieuwe activiteiten in Leupegem.
              </p>
              <p className="text-base text-gray-700 mb-8">
                Van kermis tot Sinterklaas – wij houden je op de hoogte van alle feestelijkheden.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="newsletter-form">
                <input 
                  type="email" 
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="jouw@email.be"
                  required
                  disabled={isSubscribing}
                  className="flex-1 px-5 py-4 rounded-xl border-2 border-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#0a56c2] text-lg disabled:opacity-50"
                  data-testid="newsletter-email-input"
                />
                <button 
                  type="submit"
                  disabled={isSubscribing}
                  className="px-8 py-4 bg-[#0a56c2] text-white font-bold rounded-xl hover:bg-[#083d8a] transition-all hover:scale-105 shadow-lg text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  data-testid="newsletter-submit-button"
                >
                  {isSubscribing ? 'Bezig...' : 'Inschrijven'}
                </button>
              </form>
              <p className="text-sm text-gray-700 mt-4 flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                100% veilig en spam-vrij
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-bold tracking-wider text-[#0a56c2] uppercase">Proudly Presenting</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-2" data-testid="team-title">
              De organisatorische en feestelijke brains!
            </h2>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6" data-testid="team-grid">
            {team.map((member, index) => (
              <div 
                key={member.id} 
                className="text-center card-hover"
                data-testid={`team-member-${index}`}
              >
                {member.image_url && (
                  <div className="mb-3 overflow-hidden rounded-xl">
                    <img 
                      src={getImageUrl(member.image_url)} 
                      alt={`Portretfoto van ${member.first_name} ${member.last_name}, ${member.role} bij Feestcomité Leupegem`}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}
                <h3 className="text-base font-bold text-gray-900">{member.first_name}</h3>
                <h3 className="text-base font-bold text-gray-900 mb-1">{member.last_name}</h3>
                <p className="text-xs text-gray-600 font-medium">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vrienden van het Feestcomité Section */}
      <section id="vrienden" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-yellow-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-bold tracking-wider text-[#0a56c2] uppercase">Samen maken we het verschil!</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-2">
              Vrienden van het Feestcomité
            </h2>
            <p className="text-base sm:text-lg text-gray-700 max-w-3xl mx-auto mt-6 mb-4">
              Leupegem draait op vrijwilligers! Of je nu jong of oud bent, ouder, buur of gewoon enthousiast – 
              iedereen is welkom om mee te helpen en bij te dragen aan een warm en gezellig Leupegem.
            </p>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Schrijf je hieronder in en laat ons weten hoe jij wilt helpen. Elk handje helpt!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Benefit Card 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg card-hover text-center">
              <div className="w-16 h-16 bg-[#0a56c2] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Help mee op evenementen
              </h3>
              <p className="text-sm text-gray-600">
                Sinterklaasfeest, Nieuwjaarsreceptie, Kermis... Sta mee in voor onvergetelijke momenten in Leupegem!
              </p>
            </div>

            {/* Benefit Card 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg card-hover text-center">
              <div className="w-16 h-16 bg-[#ffe980] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#0a56c2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Leer je buren beter kennen
              </h3>
              <p className="text-sm text-gray-600">
                Maak nieuwe vrienden en versterk de band met je buurt door samen te werken aan leuke projecten.
              </p>
            </div>

            {/* Benefit Card 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg card-hover text-center">
              <div className="w-16 h-16 bg-[#0a56c2] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Draag bij aan een warm Leupegem
              </h3>
              <p className="text-sm text-gray-600">
                Jouw inzet maakt het verschil! Help mee om Leupegem een nog fijnere plek te maken voor iedereen.
              </p>
            </div>
          </div>

          <div className="text-center">
            <a 
              href="/vrienden"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#0a56c2] text-white text-lg font-bold rounded-xl hover:bg-[#083d8a] transition-all hover:scale-105 shadow-xl"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Meld je aan als vriend
            </a>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section id="partners" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-bold tracking-wider text-[#0a56c2] uppercase">Ongeloofelijk veel genegenheid & dank!</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-2" data-testid="partners-title">
              Onze Partners & Sponsors
            </h2>
          </div>
          
          {partners.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8" data-testid="partners-grid">
              {partners.map((partner, index) => (
                <a
                  key={partner.id}
                  href={partner.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all hover:shadow-lg card-hover"
                  data-testid={`partner-card-${index}`}
                >
                  <img
                    src={getImageUrl(partner.logo_url)}
                    alt={`Logo van partner ${partner.name}`}
                    className="max-h-20 w-auto object-contain"
                  />
                </a>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500" data-testid="no-partners">
              Nog geen partners toegevoegd
            </p>
          )}
        </div>
      </section>

      {/* Photos Section */}
      <section id="fotos" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-bold tracking-wider text-[#0a56c2] uppercase">Memories</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-2" data-testid="photos-title">
              Foto Galerij
            </h2>
            <p className="text-lg text-gray-600 mt-4">Bekijk de mooiste momenten van onze evenementen</p>
          </div>
          
          {albums.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-testid="albums-grid">
              {albums.map((album, index) => (
                <a
                  key={album.id}
                  href={`/fotos/${album.id}`}
                  className="group"
                  data-testid={`album-card-${index}`}
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg card-hover">
                    {album.cover_image_url ? (
                      <div className="h-64 overflow-hidden relative">
                        <img
                          src={getImageUrl(album.cover_image_url)}
                          alt={`Omslagfoto van fotoalbum: ${album.title}`}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <p className="text-sm opacity-90 mb-1">{album.date}</p>
                          <h3 className="text-2xl font-bold">{album.title}</h3>
                        </div>
                      </div>
                    ) : (
                      <div className="h-64 bg-gradient-to-br from-blue-100 to-yellow-100 flex items-center justify-center">
                        <div className="text-center p-6">
                          <svg className="w-16 h-16 mx-auto mb-4 text-[#0a56c2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm text-gray-600 mb-1">{album.date}</p>
                          <h3 className="text-xl font-bold text-gray-900">{album.title}</h3>
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <p className="text-gray-600">{album.description}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 mb-12">
              <svg className="w-24 h-24 mx-auto mb-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Nog geen foto albums</h3>
              <p className="text-gray-600">Er zijn nog geen foto's toegevoegd. Kom later terug!</p>
            </div>
          )}

          {/* Facebook Link */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Meer foto's op Facebook</h3>
            <p className="text-gray-600 mb-6">
              Bekijk al onze foto's en blijf op de hoogte van onze laatste activiteiten
            </p>
            <a
              href={FACEBOOK_PHOTOS}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#1877f2] hover:bg-[#166fe5] text-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 shadow-lg btn-hover"
              data-testid="facebook-photos-link"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Bekijk meer op Facebook
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="contact-title">Contact</h2>
            <p className="text-gray-600">Vragen of opmerkingen? Stuur ons een bericht!</p>
          </div>
          
          {/* Success Message */}
          {contactSuccess && (
            <div 
              className="mb-8 p-6 bg-green-50 border border-green-200 rounded-2xl text-center animate-fade-in"
              data-testid="contact-success-message"
            >
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-green-800">Bedankt voor je bericht!</p>
                  <p className="text-green-600 text-sm">We nemen zo snel mogelijk contact op.</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Naam</label>
              <Input
                type="text"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                required
                className="w-full"
                data-testid="contact-name-input"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
                className="w-full"
                data-testid="contact-email-input"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vraag</label>
              <Textarea
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                rows={5}
                className="w-full"
                data-testid="contact-message-input"
                disabled={isSubmitting}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#0a56c2] hover:bg-[#083d8a] btn-hover"
              disabled={isSubmitting}
              data-testid="contact-submit-button"
            >
              {isSubmitting ? 'Verzenden...' : 'Verzenden'}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <img 
              src={getImageUrl(settings?.logo_url) || DEFAULT_LOGO} 
              alt="Feestcomité Leupegem logo" 
              className="h-32 w-auto mx-auto mb-4 brightness-0 invert"
            />
          </div>
          <p className="text-gray-400 mb-4">Feestcomité Leupegem - Waar elk feest onvergetelijk wordt</p>
          
          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6 mb-6 text-sm text-gray-400">
            <a 
              href="mailto:leupegemfeestcomite@gmail.com"
              className="hover:text-white transition-colors inline-flex items-center gap-2"
              data-testid="footer-email"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              leupegemfeestcomite@gmail.com
            </a>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <span className="inline-flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Dries ter Biest 23, 9700 Oudenaarde
            </span>
          </div>
          
          {/* Footer Navigation Links */}
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 mb-8 text-sm">
            <a 
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="footer-contact-link"
            >
              Contact
            </a>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <a 
              href="https://www.websitebestellen.be/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="privacy-link"
            >
              Privacybeleid
            </a>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <a 
              href={FACEBOOK_PROFILE} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors inline-flex items-center gap-1"
              data-testid="facebook-link"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </a>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <a 
              href="https://www.websitebestellen.be" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="footer-websitebestellen-link"
            >
              WebsiteBestellen.be
            </a>
          </div>
          
          <p className="text-sm text-gray-500">© {new Date().getFullYear()} Feestcomité Leupegem. Alle rechten voorbehouden.</p>
          <p className="text-xs text-gray-600 mt-4">
            Met veel liefde <span className="text-red-500">❤️</span> gemaakt door{' '}
            <a 
              href="https://www.websitebestellen.be" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-300 hover:underline transition-colors"
            >
              WebsiteBestellen.be
            </a>
          </p>
          {visitorStats && (
            <div className="mt-8 pt-6 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-2">Bezoekers</p>
              <div className="flex justify-center gap-6 flex-wrap text-sm">
                <div>
                  <span className="text-gray-400">Vandaag: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_today}</span>
                </div>
                <div>
                  <span className="text-gray-400">Deze maand: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_this_month}</span>
                </div>
                <div>
                  <span className="text-gray-400">Dit jaar: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_this_year}</span>
                </div>
                <div>
                  <span className="text-gray-400">Totaal: </span>
                  <span className="text-white font-semibold">{visitorStats.total_visits}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </footer>


      {/* Floating Action Button - Nieuwjaarsreceptie - UITGESCHAKELD (event afgelopen)
      <a
        href="/nieuwjaarsreceptie"
        className="fixed top-24 right-4 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-lg shadow-2xl transition-all hover:scale-105 z-40 flex items-center gap-2 text-sm animate-pulse"
      >
        <span className="hidden md:inline">Inschrijven Nieuwjaarsreceptie</span>
        <span className="md:hidden">Inschrijven</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </a>
      */}


      {/* Floating Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#ffe980] hover:bg-[#ffd700] text-gray-900 p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 z-50"
          data-testid="scroll-to-top-button"
          aria-label="Terug naar boven"
        >
          <svg 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={3} 
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="image-lightbox"
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
            aria-label="Sluit lightbox"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img 
            src={getImageUrl(lightboxImage)} 
            alt="Foto uit het fotoalbum van Feestcomité Leupegem - Klik buiten de afbeelding om te sluiten"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Home;